import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { useJuryStore } from '@core/frontend/stores';

export const PROJECTS_PER_PAGE = 30;

/**
 * Encapsulates all URL-driven filter, sorting, and pagination logic
 * for the Jury projects view.
 */
export function useJuryFilters(projects, isDesktop) {
    const history = useHistory();
    const location = useLocation();

    const loading = useJuryStore((s) => s.loading);
    const points = useJuryStore((s) => s.points);

    // Project IDs that were unrated when the filter was enabled. Without freezing the list,
    // a project disappears mid-input as soon as it gets rated.
    const [unratedProjectIds, setUnratedProjectIds] = useState(null);

    const queryParams = useMemo(
        () => qs.parse(location.search, { ignoreQueryPrefix: true, depth: 10 }),
        [location.search]
    );

    const selectedBadges = useMemo(() => queryParams.badges || {}, [queryParams.badges]);
    const sort = queryParams.sort || 'name';
    const q = queryParams.q || '';
    const page = parseInt(queryParams.page) || 1;
    const project = queryParams.project || null;
    const unrated = queryParams.unrated === '1';

    useEffect(() => {
        if (!unrated) {
            setUnratedProjectIds(null);
            return;
        }
        if (unratedProjectIds !== null || loading) return;

        setUnratedProjectIds(new Set(projects.filter((p) => points[p._id] === undefined).map((p) => p._id)));
    }, [unrated, loading, projects, points, unratedProjectIds]);

    /**
     * Unified helper to update URL state.
     */
    const patchParams = (patch, replace = true) => {
        const nextParams = {
            badges: Object.keys(selectedBadges).length > 0 ? selectedBadges : undefined,
            sort: sort !== 'name' ? sort : undefined,
            q: q || undefined,
            page: page !== 1 ? page : undefined,
            project: project || undefined,
            unrated: unrated ? '1' : undefined,
            ...patch,
        };

        // Clean up defaults
        if (nextParams.sort === 'name') nextParams.sort = undefined;
        if (nextParams.page === 1) nextParams.page = undefined;

        // Clean up badges that are 'all'
        if (nextParams.badges) {
            const cleanedBadges = {};
            for (const [k, v] of Object.entries(nextParams.badges)) {
                if (v && v !== 'all') cleanedBadges[k] = v;
            }
            nextParams.badges = Object.keys(cleanedBadges).length > 0 ? cleanedBadges : undefined;
        }

        const newSearch = qs.stringify(nextParams, { addQueryPrefix: true, skipNulls: true, encode: false });
        if (location.search !== newSearch) {
            if (replace) history.replace({ search: newSearch });
            else history.push({ search: newSearch });
        }
    };

    const filteredProjects = useMemo(() => {
        return projects
            .filter((p) => {
                if (unrated && unratedProjectIds && !unratedProjectIds.has(p._id)) return false;
                for (const [group, value] of Object.entries(selectedBadges)) {
                    if (!value || value === 'all') continue;
                    const hasBadgeInGroup = p.badges?.some((b) => b.group === group && b.value === value);
                    if (!hasBadgeInGroup) return false;
                }
                if (q) {
                    const lower = q.toLowerCase();
                    return (
                        p.projectTitle?.toLowerCase().includes(lower) || p.contestantName?.toLowerCase().includes(lower)
                    );
                }
                return true;
            })
            .sort((a, b) => {
                if (sort === 'name') return (a.contestantName || '').localeCompare(b.contestantName || '');
                if (sort === 'title') return (a.projectTitle || '').localeCompare(b.projectTitle || '');
                if (sort === 'voteCount') return (b.voteCount || 0) - (a.voteCount || 0);
                if (sort === 'voteCountFinale') return (b.voteCountFinale || 0) - (a.voteCountFinale || 0);
                return 0;
            });
    }, [projects, selectedBadges, sort, q, unrated, unratedProjectIds]);

    const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);

    const currentProjects = useMemo(() => {
        return filteredProjects.slice((page - 1) * PROJECTS_PER_PAGE, page * PROJECTS_PER_PAGE);
    }, [filteredProjects, page]);

    // Behavioral Effects (Auto-correction)

    // Ensure we are on the correct page for the selected project.
    useEffect(() => {
        if (loading || !project) return;
        const index = filteredProjects.findIndex((p) => p._id === project);
        if (index !== -1) {
            const correctPage = Math.floor(index / PROJECTS_PER_PAGE) + 1;
            if (correctPage !== page) {
                patchParams({ page: correctPage }, true);
            }
        }
    }, [loading, project, filteredProjects, page]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-select first visible on desktop.
    useEffect(() => {
        if (!isDesktop || loading) return;
        const isVisibleOnPage = currentProjects.some((p) => p._id === project);
        if (!isVisibleOnPage) {
            patchParams({ project: currentProjects[0]?._id ?? null }, true);
        }
    }, [isDesktop, currentProjects, project, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // 4. Action Handlers
    const handlePageChange = (pageNumber) => {
        const patch = { page: pageNumber };
        if (isDesktop) {
            const index = (pageNumber - 1) * PROJECTS_PER_PAGE;
            const firstOnPage = filteredProjects[index];
            if (firstOnPage) patch.project = firstOnPage._id;
        }
        patchParams(patch);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCardSelect = (p) => {
        const isClosingOnMobile = !isDesktop && project === p._id;
        patchParams({ project: isClosingOnMobile ? null : p._id });

        if (!isClosingOnMobile) {
            setTimeout(() => {
                const elementId = isDesktop ? 'jury-details-pane' : `project-card-${p._id}`;
                document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    };

    const resetFilters = () => patchParams({ badges: undefined, q: '', page: 1, project: null, unrated: undefined });

    return {
        // State
        selectedBadges,
        sort,
        q,
        page,
        project,
        unrated,
        filteredProjects,
        currentProjects,
        totalPages,
        // Handlers
        patchParams,
        handlePageChange,
        handleCardSelect,
        resetFilters,
    };
}
